import { useCallback, useContext, useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchWhatsappQrCode } from "@/modules/common/api/index.v2";
import useAuth from "@/modules/common/hooks/auth.hook";
import { NawyPassportContext } from "@/modules/common/context/nawy-passport.provider";
import { WhatsappInstanceStatus } from "@/modules/common/types";
import useCooldown, { isStoredCooldownActive } from "./use-cooldown.hook";

/** Minimum gap between two QR/pairing-code requests. */
export const QR_COOLDOWN_SECONDS = 30;

/** How often the passport is re-read while the relink screen is open. */
const PASSPORT_POLL_MS = 5_000;

interface QrSnapshot {
  base64?: string;
  pairingCode?: string;
}

export interface WhatsappQrState {
  qrCodeSrc: string | undefined;
  pairingCode: string | undefined;
  isFetching: boolean;
  isError: boolean;
  /** True once the passport reports the instance as linked. */
  isConnected: boolean;
  /** Requests a fresh QR + pairing code; ignored while cooling down or in flight. */
  refresh: () => void;
  canRefresh: boolean;
  cooldownSeconds: number;
}

// Keyed per user so a shared browser never inherits another agent's code.
const cooldownKeyFor = (username: string | undefined) =>
  username ? `whatsappQrCooldown:${username}` : undefined;
const snapshotKeyFor = (username: string | undefined) =>
  username ? `whatsappQrSnapshot:${username}` : undefined;

const readSnapshot = (storageKey: string | undefined): QrSnapshot | null => {
  if (!storageKey) return null;
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as QrSnapshot;
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
};

const writeSnapshot = (storageKey: string | undefined, snapshot: QrSnapshot) => {
  if (!storageKey) return;
  try {
    localStorage.setItem(storageKey, JSON.stringify(snapshot));
  } catch {
    /* noop */
  }
};

const clearKey = (storageKey: string | undefined) => {
  if (!storageKey) return;
  try {
    localStorage.removeItem(storageKey);
  } catch {
    /* noop */
  }
};

const toDataUrl = (base64: string | undefined) => {
  if (!base64) return undefined;
  return base64.startsWith("data:") ? base64 : `data:image/png;base64,${base64}`;
};

/**
 * Owns the QR/pairing code for the relink screens.
 *
 * The QR endpoint is only hit on mount and on an explicit refresh. A successful
 * link is detected by re-reading the passport instead, so scanning the code does
 * not depend on the QR request being repeated.
 *
 * The cooldown and the last code are persisted, so reloading the page neither
 * skips the wait nor leaves the screen blank. Note this is a client-side
 * deterrent only — clearing site data resets it.
 */
const useWhatsappQr = ({ enabled = true }: { enabled?: boolean } = {}): WhatsappQrState => {
  const { user } = useAuth();
  const username = user?.["cognito:username"];
  const { whatsappInstance } = useContext(NawyPassportContext);
  const queryClient = useQueryClient();

  const cooldownKey = cooldownKeyFor(username);
  const snapshotKey = snapshotKeyFor(username);

  const { secondsLeft, isCooling, start } = useCooldown(QR_COOLDOWN_SECONDS, {
    storageKey: cooldownKey,
  });

  /**
   * Only a cooldown restored from storage defers the request. A button press
   * must never arm this, or every press would queue a second fetch when its own
   * cooldown ends — reintroducing the polling this screen was built to avoid.
   */
  const [deferredFetch, setDeferredFetch] = useState(() =>
    isStoredCooldownActive(cooldownKey),
  );
  const [snapshot, setSnapshot] = useState<QrSnapshot | null>(() =>
    readSnapshot(snapshotKey),
  );

  // The key depends on the signed-in user, which can arrive after first render.
  useEffect(() => {
    if (isStoredCooldownActive(cooldownKey)) setDeferredFetch(true);
  }, [cooldownKey]);

  useEffect(() => {
    if (deferredFetch && !isCooling) setDeferredFetch(false);
  }, [deferredFetch, isCooling]);

  const { data, isFetching, isError, refetch } = useQuery(
    ["whatsappQrCode", username],
    () => fetchWhatsappQrCode(username),
    {
      enabled: enabled && !!username && !deferredFetch,
      retry: false,
      refetchOnWindowFocus: false,
      refetchInterval: false,
      onSuccess: (res) => {
        const next: QrSnapshot = {
          base64: res?.data?.base64,
          pairingCode: res?.data?.pairingCode,
        };
        setSnapshot(next);
        writeSnapshot(snapshotKey, next);
      },
    },
  );

  useEffect(() => {
    if (!enabled) return;

    const id = setInterval(() => {
      queryClient.invalidateQueries(["userPassport"]);
    }, PASSPORT_POLL_MS);

    return () => clearInterval(id);
  }, [enabled, queryClient]);

  const isConnected =
    whatsappInstance?.status === WhatsappInstanceStatus.Open;

  // The code is a linking credential; drop it as soon as it is spent.
  useEffect(() => {
    if (!isConnected) return;
    clearKey(cooldownKey);
    clearKey(snapshotKey);
    setSnapshot(null);
  }, [isConnected, cooldownKey, snapshotKey]);

  // A failed request leaves nothing to scan, so the retry stays available even
  // mid-cooldown. Never while one is in flight, or a retry would double up.
  const canRefresh = !isFetching && (isError || !isCooling);

  const refresh = useCallback(() => {
    if (!canRefresh) return;
    start();
    refetch();
  }, [canRefresh, start, refetch]);

  const payload = data?.data ?? snapshot ?? undefined;

  return {
    qrCodeSrc: toDataUrl(payload?.base64),
    pairingCode: payload?.pairingCode,
    isFetching,
    isError,
    isConnected,
    refresh,
    canRefresh,
    cooldownSeconds: secondsLeft,
  };
};

export default useWhatsappQr;
